import { Timestamp } from 'firebase/firestore';

// ============================================
// ROOM/AREA TYPES
// ============================================
export type RoomType =
  | 'kitchen'
  | 'bathroom'
  | 'basement'
  | 'living_room'
  | 'bedroom'
  | 'outdoor'
  | 'garage'
  | 'home_office'
  | 'other';

// Alias for user-facing terminology (internally still RoomType)
export type AreaType = RoomType;

// ============================================
// TENANT (Contractor Company)
// ============================================

/**
 * @deprecated Quote/pricing functionality has been removed from the widget.
 * This interface is kept for backward compatibility with existing data.
 * Do not use for new features.
 */
export interface PricingConfig {
  // Base costs per room type
  base_costs: {
    kitchen: number;
    bathroom: number;
    basement: number;
    living_room: number;
    bedroom: number;
    outdoor: number;
    garage: number;
    home_office: number;
  };
  // Size multipliers
  size_multipliers: {
    small: number;
    medium: number;
    large: number;
  };
  // Condition multipliers
  condition_multipliers: {
    good: number;
    fair: number;
    poor: number;
  };
  // Selection add-on costs
  selection_costs: {
    flooring: { standard: number; premium: number };
    walls: { paint: number; wallpaper: number; tile: number };
    cabinets: { refinish: number; replace: number };
    countertops: { laminate: number; quartz: number; granite: number };
    lighting: { basic: number; recessed: number; custom: number };
    fixtures: { standard: number; premium: number };
  };
}

// ============================================
// SOCIAL PROOF TYPES
// ============================================
export interface WidgetReview {
  text: string;
  reviewer_name: string;
  avatar_url?: string;
  date?: string;
  rating?: number; // 1-5, default 5
}

export type TrustBadgeType = 'bbb' | 'licensed' | 'insured' | 'bonded' | 'custom';

export interface TrustBadge {
  type: TrustBadgeType;
  enabled: boolean;
  image_url?: string;
  label?: string;
}

export interface BeforeAfterProject {
  before_url: string;
  after_url: string;
  project_type: string;
  description?: string;
}

// ============================================
// ROOM/AREA CONFIGURATION TYPES
// ============================================

/**
 * @deprecated Use ProjectTypeConfig instead.
 * Kept for backward compatibility with existing data.
 */
export interface RoomConversionOption {
  id: string;
  label: string;
  description: string;
}

/**
 * Configuration for a single selection option within a category
 */
export interface SelectionOptionConfig {
  id: string;
  label: string;
  description?: string;
}

/**
 * Configuration for a selection category (e.g., "Decking Material", "Railing Style")
 */
export interface SelectionCategoryConfig {
  id: string;
  label: string;
  allowColor?: boolean; // Shows color picker if true
  options: SelectionOptionConfig[];
}

/**
 * Configuration for a project type (e.g., "Deck", "Pergola", "ADU")
 * Replaces the old conversion_options concept with more flexibility
 */
export interface ProjectTypeConfig {
  id: string;
  label: string;
  description?: string;
  selectionCategories?: SelectionCategoryConfig[];
}

export interface RoomOverride {
  label?: string;
  description?: string;
  /**
   * @deprecated Use projectTypes instead
   */
  conversion_options?: RoomConversionOption[];
  /**
   * Project types available for this area (e.g., Deck, Patio, Pergola for Outdoor)
   * Supports multi-select in the widget
   */
  projectTypes?: ProjectTypeConfig[];
}

export interface RoomConfig {
  enabled_rooms: RoomType[];
  room_overrides?: Partial<Record<RoomType, RoomOverride>>;
}

// ============================================
// WIDGET CONFIG
// ============================================
export interface WidgetConfig {
  enabled: boolean;
  cta_book_call_url?: string;
  cta_get_quote_url?: string;
  business_phone?: string;
  watermark_text?: string;
  // Lead capture requirements
  require_address?: boolean; // default: true
  require_timeline?: boolean; // default: true
  // Default country for phone validation (hides country toggle if set)
  default_country?: 'US' | 'CA';
  // Sherwin-Williams color selection
  featured_paint_colors?: string[]; // Array of SW codes tenant showcases
  allow_color_search?: boolean; // default: true - allow customers to search all colors
  // Email opt-in configuration (GDPR/CAN-SPAM compliant)
  email_opt_in_enabled?: boolean; // default: true - show opt-in checkbox
  email_opt_in_text?: string; // Customizable opt-in text
  email_opt_in_default?: boolean; // default: false (GDPR requires unchecked)
  // Social proof (displayed during processing)
  reviews?: WidgetReview[];
  trust_badges?: TrustBadge[];
  before_after_projects?: BeforeAfterProject[];
  // Room configuration
  room_config?: RoomConfig;
}

// ============================================
// BRANDING CONFIG
// ============================================
export interface BrandingConfig {
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  background_color?: string;
  button_text_color?: string;
}

// ============================================
// SHERWIN-WILLIAMS COLORS
// ============================================
export interface SherwinWilliamsColor {
  code: string; // e.g., "SW 7029"
  name: string; // e.g., "Agreeable Gray"
  hex: string; // e.g., "#D1CBC4"
  family: string; // e.g., "neutral", "gray", "blue"
  lrv?: number; // Light Reflectance Value (0-100)
}

export interface TenantColorConfig {
  featured_paint_colors: string[]; // Array of SW color codes
  allow_color_search: boolean; // Allow customers to search all colors
}

/**
 * Maximum number of custom colors a tenant can store
 */
export const MAX_CUSTOM_COLORS = 50;

/**
 * Validation result for custom colors operations
 * Used to validate custom colors array before saving to Firestore
 */
export interface CustomColorsValidation {
  isValid: boolean;
  error?: string;
  currentCount?: number;
  maxCount?: number;
}

// ============================================
// ADDRESS (US/Canada)
// ============================================
export interface Address {
  street: string;
  city: string;
  state: string; // US state or Canadian province
  zip: string; // US ZIP or Canadian postal code
  country: 'US' | 'CA';
}

// ============================================
// PROJECT TIMELINE
// ============================================
export type ProjectTimeline =
  | 'asap'
  | '30_days'
  | '60_days'
  | '90_days'
  | 'exploring';

export const PROJECT_TIMELINE_LABELS: Record<ProjectTimeline, string> = {
  asap: 'As soon as possible',
  '30_days': 'Within 30 days',
  '60_days': 'Within 60 days',
  '90_days': 'Within 90 days',
  exploring: 'Just exploring options',
};

export interface TenantDoc {
  company_name: string;
  slug: string;
  pricing_config: PricingConfig;
  branding_config: BrandingConfig;
  widget_config: WidgetConfig;
  color_config?: TenantColorConfig; // Sherwin-Williams color preferences
  /**
   * Custom color palette for this tenant (max 50 colors)
   * These colors are synced to the widgetPublic collection for public widget access.
   * Custom colors can be referenced in widget_config.featured_paint_colors using their code.
   */
  custom_colors?: SherwinWilliamsColor[];
  // Stripe (for future use)
  stripe_customer_id?: string;
  subscription_status: 'trial' | 'active' | 'past_due' | 'canceled';
  subscription_tier: 'free' | 'pro' | 'enterprise';
  trial_ends_at?: Timestamp;
  generations_this_month: number;
  // Credit-based subscription system
  generation_credits_monthly?: number;
  generation_credits_addon?: number;
  generation_credits_rollover?: number;
  credits_cap?: number;
  last_credit_reset_at?: Timestamp;
  zero_credits_notified?: boolean;
  // Webhook integration
  webhook_url?: string;
  webhook_enabled?: boolean;
  // Email notifications (enabled by default)
  email_notifications_enabled?: boolean;
  created_at: Timestamp;
}

// ============================================
// USER (Team Member within Tenant)
// ============================================
export interface UserDoc {
  email: string;
  role: 'owner' | 'admin' | 'member';
  created_at: Timestamp;
}

// ============================================
// LEAD (Homeowner Submission)
// ============================================
export interface GeminiAnalysis {
  room_type: string;
  room_state: string;
  materials: string[];
  notes: string;
}

export interface LeadDoc {
  client_name: string;
  client_phone: string;
  client_ip?: string;
  verification_status: 'unverified' | 'pending' | 'verified';
  callback_requested?: boolean;
  gemini_analysis: GeminiAnalysis;
  created_at: Timestamp;
}

// ============================================
// GENERATION (AI-Generated Design)
// ============================================
export interface PromptParameters {
  style: string;
  floor?: string;
  walls?: string;
  cabinets?: string;
  [key: string]: string | undefined;
}

export interface GenerationDoc {
  original_image_path: string;
  generated_image_path: string;
  watermarked_image_path: string;
  prompt_parameters: PromptParameters;
  quote_range_min: number;
  quote_range_max: number;
  created_by: 'client' | 'contractor';
  created_at: Timestamp;
}

// ============================================
// WIDGET SESSION (Lead from embedded widget)
// ============================================
export type WidgetSessionStatus =
  | 'started'
  | 'uploading'
  | 'processing'
  | 'generated'
  | 'phone_sent'
  | 'verified'
  | 'abandoned';

export interface RenovationSelections {
  flooring?: { type: string; tier: 'standard' | 'premium' };
  walls?: {
    type: 'paint' | 'wallpaper' | 'tile';
    color?: SherwinWilliamsColor; // Full SW color object for consistent type safety
  };
  cabinets?: {
    style: 'refinish' | 'replace';
    color?: SherwinWilliamsColor; // Full SW color object for consistent type safety
  };
  countertops?: { material: 'laminate' | 'quartz' | 'granite' };
  lighting?: { type: 'basic' | 'recessed' | 'custom' };
  fixtures?: { tier: 'standard' | 'premium' };
  style_preference?: string;
  additional_notes?: string;
  // Dynamic selections for custom categories (keyed by category id)
  [key: string]: unknown;
}

/**
 * Selections organized by project type for multi-select scenarios
 * e.g., { deck: { decking_material: 'composite' }, pergola: { structure: 'vinyl' } }
 */
export type ProjectTypeSelections = Record<string, RenovationSelections>;

export interface WidgetContact {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  // Email marketing opt-in (GDPR compliant - default false)
  email_opt_in?: boolean;
  email_opt_in_timestamp?: string; // ISO timestamp when opt-in was given
  // Enhanced lead capture fields (collected at phone gate step)
  address?: Address;
  project_timeline?: ProjectTimeline;
  additional_info?: string;
}

export interface WidgetGeminiAnalysis {
  room_type: RoomType;
  room_size: 'small' | 'medium' | 'large';
  current_condition: 'good' | 'fair' | 'poor';
  complexity_factors: string[];
  estimated_sqft?: number;
  detected_elements: string[];
  notes: string;
}

export interface WidgetSessionDoc {
  tenant_id: string;
  status: WidgetSessionStatus;

  // User selections
  room_type: RoomType;
  /**
   * Selected project types for multi-select (e.g., ['deck', 'pergola'])
   * Empty array if no project types selected or area doesn't have project types
   */
  project_types: string[];
  original_images: string[]; // Firebase Storage paths
  /**
   * For areas without project types, uses flat selections.
   * For areas with project types, use project_type_selections instead.
   */
  selections: RenovationSelections;
  /**
   * Selections organized by project type for multi-select scenarios
   * e.g., { deck: { decking_material: 'composite' }, pergola: { structure: 'vinyl' } }
   */
  project_type_selections?: ProjectTypeSelections;
  contact: WidgetContact;

  // n8n response data
  gemini_analysis?: WidgetGeminiAnalysis;
  generated_image?: string; // Full resolution image path
  blurred_preview?: string; // Blurred/watermarked preview path

  // Calculated on frontend after n8n response
  quote_range?: { min: number; max: number };

  // Phone verification
  verification_code?: string;
  verification_expires?: Timestamp;
  revision_count: number; // Max 1

  // Rate limiting & metadata
  client_ip?: string;
  user_agent?: string;
  source_url?: string; // URL where widget is embedded

  created_at: Timestamp;
  updated_at: Timestamp;
}

// ============================================
// WIDGET ANALYTICS
// ============================================
export type WidgetAnalyticsEvent =
  | 'widget_opened'
  | 'room_selected'
  | 'images_uploaded'
  | 'selections_made'
  | 'contact_submitted'
  | 'generation_started'
  | 'generation_completed'
  | 'phone_entered'
  | 'code_sent'
  | 'code_verified'
  | 'full_image_viewed'
  | 'cta_clicked'
  | 'revision_requested'
  | 'abandoned';

export interface WidgetAnalyticsDoc {
  tenant_id: string;
  session_id: string;
  event: WidgetAnalyticsEvent;
  metadata?: Record<string, unknown>;
  timestamp: Timestamp;
}

// ============================================
// WITH ID VARIANTS (for use after fetching)
// ============================================
export interface Tenant extends TenantDoc {
  id: string;
}

export interface User extends UserDoc {
  id: string;
}

export interface Lead extends LeadDoc {
  id: string;
  tenant_id: string;
}

export interface Generation extends GenerationDoc {
  id: string;
  lead_id: string;
  tenant_id: string;
}

export interface WidgetSession extends WidgetSessionDoc {
  id: string;
}

export interface WidgetAnalytics extends WidgetAnalyticsDoc {
  id: string;
}

// ============================================
// COMBINED TYPES (for dashboard display)
// ============================================
export interface LeadWithGenerations extends Lead {
  generations: Generation[];
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Convert Firestore Timestamp to ISO string
 */
export function timestampToISO(timestamp: Timestamp | undefined): string {
  if (!timestamp) return new Date().toISOString();
  return timestamp.toDate().toISOString();
}

/**
 * Convert Firestore document to Lead with string dates
 */
export function convertLeadDoc(id: string, tenantId: string, doc: LeadDoc): Lead {
  return {
    ...doc,
    id,
    tenant_id: tenantId,
  };
}

/**
 * Convert Firestore document to Generation with string dates
 */
export function convertGenerationDoc(
  id: string,
  leadId: string,
  tenantId: string,
  doc: GenerationDoc
): Generation {
  return {
    ...doc,
    id,
    lead_id: leadId,
    tenant_id: tenantId,
  };
}

/**
 * @deprecated Quote/pricing functionality has been removed from the widget.
 * This function is kept for backward compatibility with existing data.
 * Do not use for new features.
 */
export function getDefaultPricingConfig(): PricingConfig {
  return {
    base_costs: {
      kitchen: 18000,
      bathroom: 10000,
      basement: 15000,
      living_room: 8000,
      bedroom: 6000,
      outdoor: 12000,
      garage: 8000,
      home_office: 7000,
    },
    size_multipliers: {
      small: 0.7,
      medium: 1.0,
      large: 1.4,
    },
    condition_multipliers: {
      good: 0.85,
      fair: 1.0,
      poor: 1.25,
    },
    selection_costs: {
      flooring: { standard: 3000, premium: 6000 },
      walls: { paint: 1500, wallpaper: 3000, tile: 5000 },
      cabinets: { refinish: 4000, replace: 12000 },
      countertops: { laminate: 2000, quartz: 5000, granite: 6000 },
      lighting: { basic: 1000, recessed: 2500, custom: 5000 },
      fixtures: { standard: 1500, premium: 4000 },
    },
  };
}

/**
 * Default enabled areas for new tenants
 * Common indoor areas enabled by default; outdoor, garage, home_office, other disabled
 */
export const DEFAULT_ENABLED_AREAS: RoomType[] = [
  'kitchen',
  'bathroom',
  'basement',
  'living_room',
  'bedroom',
];

/**
 * Default widget configuration for new tenants
 */
export function getDefaultWidgetConfig(): WidgetConfig {
  return {
    enabled: true,
    watermark_text: undefined, // Will use company_name if not set
    require_address: true,
    require_timeline: true,
    featured_paint_colors: undefined, // Uses default featured colors from SW data
    allow_color_search: true,
    // Email opt-in defaults (GDPR compliant)
    email_opt_in_enabled: true,
    email_opt_in_text: "I'd like to receive design inspiration, tips, and special offers via email. You can unsubscribe anytime.",
    email_opt_in_default: false, // GDPR requires unchecked by default
    // Social proof defaults (empty - tenant configures)
    reviews: undefined,
    trust_badges: undefined,
    before_after_projects: undefined,
    // Room config defaults (common indoor areas enabled)
    room_config: {
      enabled_rooms: DEFAULT_ENABLED_AREAS,
    },
  };
}

/**
 * Default room/area configuration for new tenants
 */
export function getDefaultRoomConfig(): RoomConfig {
  return {
    enabled_rooms: DEFAULT_ENABLED_AREAS,
  };
}

/**
 * Default branding configuration for new tenants
 */
export function getDefaultBrandingConfig(): BrandingConfig {
  return {
    primary_color: '#2563eb', // Default blue
  };
}

/**
 * Default color configuration for new tenants
 */
export function getDefaultColorConfig(): TenantColorConfig {
  return {
    featured_paint_colors: [],
    allow_color_search: true,
  };
}

/**
 * @deprecated Quote/pricing functionality has been removed from the widget.
 * This function is kept for backward compatibility with existing data.
 * Do not use for new features.
 */
export function calculateQuoteRange(
  pricingConfig: PricingConfig,
  geminiAnalysis: WidgetGeminiAnalysis,
  selections: RenovationSelections
): { min: number; max: number } {
  const { base_costs, size_multipliers, condition_multipliers, selection_costs } = pricingConfig;

  // Base cost for room type
  let cost = base_costs[geminiAnalysis.room_type] || 10000;

  // Apply size multiplier
  cost *= size_multipliers[geminiAnalysis.room_size] || 1.0;

  // Apply condition multiplier
  cost *= condition_multipliers[geminiAnalysis.current_condition] || 1.0;

  // Add selection costs
  if (selections.flooring?.tier) {
    cost += selection_costs.flooring[selections.flooring.tier] || 0;
  }
  if (selections.walls?.type) {
    cost += selection_costs.walls[selections.walls.type] || 0;
  }
  if (selections.cabinets?.style) {
    cost += selection_costs.cabinets[selections.cabinets.style] || 0;
  }
  if (selections.countertops?.material) {
    cost += selection_costs.countertops[selections.countertops.material] || 0;
  }
  if (selections.lighting?.type) {
    cost += selection_costs.lighting[selections.lighting.type] || 0;
  }
  if (selections.fixtures?.tier) {
    cost += selection_costs.fixtures[selections.fixtures.tier] || 0;
  }

  // Apply complexity factor (15% per factor)
  const complexityMultiplier = 1 + (geminiAnalysis.complexity_factors?.length || 0) * 0.15;
  cost *= complexityMultiplier;

  // Return range (±20%)
  return {
    min: Math.round(cost * 0.8),
    max: Math.round(cost * 1.2),
  };
}
